// VOY-417 §4 — Keywords Voy remembers — READ-ONLY HERE.
//
// Source-colored pill tags from the keyword store (`source` field per
// VOY-438 enrichment: chat / quiz / behavior / user / legacy_migration).
// Three v6-palette colors per the design; "user" and "legacy_migration"
// share the neutral pill.
//
// The keyword EDITOR (add / weight / reorder / delete) lives on the
// Profile page (VOY-485). A small "Edit keywords" link deep-links there.

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { getEnrichedKeywords } from '@/lib/profile'
import type { KeywordSource, EnrichedKeyword } from '@/types/profile'

export interface KeywordsSectionProps {
  userId: string
}

function pillClassForSource(s: KeywordSource | null | undefined): string {
  // Three distinct v6-palette colors per the ticket:
  //   chat     → accent (gold)
  //   quiz     → purple
  //   behavior → mint
  // Everything else → neutral pill.
  if (s === 'chat') return 'pill accent'
  if (s === 'quiz') return 'pill purple'
  if (s === 'behavior') return 'pill mint'
  return 'pill'
}

function sourceLabel(s: KeywordSource | null | undefined): string {
  switch (s) {
    case 'chat': return 'from chat'
    case 'quiz': return 'from quiz'
    case 'behavior': return 'observed'
    case 'user': return 'declared'
    case 'legacy_migration': return 'imported'
    default: return ''
  }
}

export function KeywordsSection({ userId }: KeywordsSectionProps) {
  // Bot review #1 fix: was using getKeywordPreferences which strips
  // `source` to the legacy {keyword, weight} pair, so all pills
  // rendered as neutral. getEnrichedKeywords returns the full
  // EnrichedKeyword shape, preserving the source provenance the
  // ticket requires for color-coding.
  const [keywords, setKeywords] = useState<EnrichedKeyword[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!userId) return
    void (async () => {
      setLoading(true)
      try {
        setKeywords(await getEnrichedKeywords(userId))
      } finally {
        setLoading(false)
      }
    })()
  }, [userId])

  const enriched = keywords.map((k, i) => ({
    text: k.text,
    weight: k.weight,
    source: (k.source ?? null) as KeywordSource | null,
    rank: i,
  }))

  return (
    <section className="card" aria-labelledby="keywords-h">
      <div className="card-h">
        <h3 id="keywords-h">Keywords Voy remembers</h3>
        <span className="meta">{loading ? 'LOADING…' : 'FROM YOUR CHATS, QUIZ &amp; HISTORY'}</span>
      </div>

      {!loading && enriched.length === 0 ? (
        <div className="you-empty-state">
          <p className="you-empty-headline">No keywords yet.</p>
          <p className="you-empty-sub">
            Chat with Voy or take the travel quiz — keywords like &quot;boutique&quot;, &quot;sushi&quot;, or &quot;jazz&quot;
            show up here as the agent picks them up.
            {' '}
            <Link to="/profile#keywords-h" className="you-inline-link">Add them now <ArrowRight aria-hidden="true" /></Link>
          </p>
        </div>
      ) : (
        <>
          <div className="you-keyword-pills" role="list" aria-label="Keywords Voy remembers">
            {enriched.map((k) => (
              <span key={k.text} className={pillClassForSource(k.source)} role="listitem" title={sourceLabel(k.source) || undefined}>
                {k.text}
              </span>
            ))}
          </div>
          <div className="you-keyword-legend">
            <LegendDot className="accent" /> chat
            <LegendDot className="purple" /> quiz
            <LegendDot className="mint" /> observed
            <Link to="/profile#keywords-h" className="you-inline-link" style={{ marginLeft: 'auto' }}>
              Edit keywords <ArrowRight aria-hidden="true" />
            </Link>
          </div>
        </>
      )}
    </section>
  )
}

function LegendDot({ className }: { className: 'accent' | 'purple' | 'mint' }) {
  return <span className={`you-legend-dot ${className}`} aria-hidden="true" />
}
